/**
 * LinkedList.ts
 * @author Diao Zheng
 * @file A doubly-linked list data structure
 */
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var _remove, _add, _addOne;
import { LinkedListItem } from "./LinkedListItem";
import { isNone, isSome } from "./Option";
export class LinkedList {
    constructor(...items) {
        this.length = 0;
        this.push = (value) => this.addToEnd(value);
        this.pop = () => this.removeFromEnd();
        this.shift = () => this.removeFromFront();
        this.unshift = (value) => this.addToFront(value);
        _remove.set(this, (value, list) => {
            if (list !== this) {
                return;
            }
            if (!--this.length) {
                this.head = undefined;
                this.tail = undefined;
            }
            else if (value === this.head) {
                this.head = value.next;
            }
            else if (value === this.tail) {
                this.tail = value.previous;
            }
        });
        _add.set(this, (next, node, list) => {
            if (list !== this) {
                return;
            }
            ++this.length;
            if (node === this.tail) {
                this.tail = next;
            }
            if (next === this.head) {
                this.head = node;
            }
        });
        _addOne.set(this, (value) => {
            const item = new LinkedListItem(value, this, __classPrivateFieldGet(this, _remove), __classPrivateFieldGet(this, _add));
            this.head = item;
            this.tail = item;
            this.length = 1;
        });
        this.addToEnd = (value) => {
            if (isSome(this.tail)) {
                this.tail.addAfter(value);
            }
            else {
                __classPrivateFieldGet(this, _addOne).call(this, value);
            }
        };
        this.addToFront = (value) => {
            if (isSome(this.head)) {
                this.head.addBefore(value);
            }
            else {
                __classPrivateFieldGet(this, _addOne).call(this, value);
            }
        };
        this.recomputeLength = () => {
            this.length = 0;
            for (let i = this.head; isSome(i); i = i.next) {
                ++this.length;
            }
            return this.length;
        };
        this.removeFromEnd = () => {
            if (isNone(this.tail)) {
                return;
            }
            const item = this.tail.popSelf();
            return item.value;
        };
        this.removeFromFront = () => {
            if (isNone(this.head)) {
                return;
            }
            const item = this.head.popSelf();
            return item.value;
        };
        for (const item of items) {
            this.addToEnd(item);
        }
    }
    getHead() {
        return this.head;
    }
    drain(effect) {
        let item;
        // tslint:disable-next-line: no-conditional-assignment
        while (item = this.removeFromFront()) {
            effect(item);
        }
    }
    *[(_remove = new WeakMap(), _add = new WeakMap(), _addOne = new WeakMap(), Symbol.iterator)]() {
        for (let i = this.head; isSome(i); i = i.next) {
            yield i.value;
        }
    }
}
